import { useState } from 'react';

import { ValidationErrors } from '../../../../modals/order-method/types';
import { GooglePlaceResource } from '../../../../services/resources/GooglePlaceResource';
import { DeliveryAddressSchema } from '../../modals/delivery-address/form/schema';
import { CompanyDetailsInterface, GoogleLocationInterface } from '../../../../services/exports/Interfaces';
import { selectCompany } from '../../../../store/slices/InitialDataSlice';
import { useSelector } from 'react-redux';

const normalizePostalCode = (postalCode?: null | string): string =>
  (postalCode ?? '').replace(/\s+/g, '').toUpperCase();

const useGetDeliveryLocation = (company: CompanyDetailsInterface) => {
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<ValidationErrors>(null);
  const franchise = useSelector(selectCompany);

  async function geocode(data: DeliveryAddressSchema): Promise<GoogleLocationInterface<google.maps.LatLng>> {
    const companyCountry = company?.country ?? franchise.country;
    const country = data.delivery_country ?? companyCountry;
    const address = {
      streetName: data.delivery_street_name,
      streetNumber: data.delivery_street_number,
      zip: data.delivery_zip_code,
      city: data.delivery_city,
    };

    const res = await new GooglePlaceResource().getLocation({
      ...address,
      // The company's state means nothing for an address across the border.
      state: country === companyCountry ? (company?.state ?? franchise.state) : undefined,
      country,
    });

    const typedPostalCode = normalizePostalCode(data.delivery_zip_code);

    if (data.delivery_country || (res.success && normalizePostalCode(res.postalCode) === typedPostalCode)) {
      return res;
    }

    // A hand-typed address has no country of its own, and the company's country hides one across
    // the border. Look again near the outlet, but only trust a match on the typed postcode — the
    // country restriction exists because a same-named street elsewhere is otherwise returned.
    const bounds = GooglePlaceResource.autocompleteBounds([
      { lat: company?.lat ?? franchise.lat, lng: company?.lon ?? franchise.lon },
    ]);

    if (!bounds) {
      return res;
    }

    const nearby = await new GooglePlaceResource().getLocation({ ...address, bounds });

    return nearby.success && normalizePostalCode(nearby.postalCode) === typedPostalCode ? nearby : res;
  }

  async function getLocation(
    data: DeliveryAddressSchema,
    selectedPlace?: GooglePlaceResource,
  ): Promise<GoogleLocationInterface<google.maps.LatLng>> {
    setValidationError(null);

    if (selectedPlace) {
      return { success: true, location: selectedPlace.getCoordinates(), country: selectedPlace.getCountry() };
    }

    setLoading(true);

    const res = await geocode(data);

    setLoading(false);

    if (!res.success) {
      setValidationError(ValidationErrors.address_not_found);
    }

    return res;
  }

  const resetError = () => setValidationError(null);

  return { loading, validationError, getLocation, resetError };
};

export default useGetDeliveryLocation;
